import { useState, useEffect, useRef, type ReactElement } from "react";
import { useDialogA11y } from "../../hooks/useDialogA11y";

export default function QuickChannelModal({
  open,
  busy,
  onClose,
  onSubmit,
  types = [],
}: {
  open: boolean;
  busy?: boolean;
  onClose: () => void;
  onSubmit: (name: string, channelType: string) => void;
  types?: { id: string; label: string }[];
}): ReactElement | null {
  const [name, setName] = useState("");
  const [channelType, setChannelType] = useState(types[0]?.id || "text");

  useEffect(() => {
    if (open) {
      setName("");
      setChannelType(types[0]?.id || "text");
    }
  }, [open, types]);

  const dialogRef = useRef<HTMLDivElement>(null);
  useDialogA11y(open, dialogRef, onClose);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[7000] flex items-center justify-center bg-black/70 px-4" onClick={onClose}>
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="quick-channel-title"
        tabIndex={-1}
        className="w-full max-w-md rounded-2xl border border-white/10 bg-neutral-950/95 p-5 text-white shadow-2xl outline-none"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-xs text-fg-2">New channel</div>
            <div id="quick-channel-title" className="text-lg font-medium">Create in current space</div>
          </div>
          <button
            type="button"
            className="rounded-full border border-white/10 p-2 text-fg-2 hover:text-white"
            onClick={onClose}
            aria-label="Close"
            disabled={busy}
          >
            ×
          </button>
        </div>

        <div className="mt-4 space-y-3">
          <label className="block text-sm text-fg">
            Channel name
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-1 w-full rounded-xl border border-white/10 bg-black/40 px-3 py-2 text-sm text-white placeholder-neutral-500 focus:border-emerald-400 focus:outline-none"
              placeholder="announcements, calendar, project-x..."
              disabled={busy}
              autoFocus
            />
          </label>

          <div className="space-y-2">
            <div className="text-xs text-fg-3">Channel type</div>
            <div className="grid grid-cols-2 gap-2">
              {types.map((t) => (
                <label
                  key={t.id}
                  className={`flex items-center gap-2 rounded-xl border px-3 py-2 text-sm ${
                    channelType === t.id ? "border-emerald-400/70 bg-emerald-500/10" : "border-white/10 bg-black/30"
                  }`}
                >
                  <input
                    type="radio"
                    name="channel-type"
                    value={t.id}
                    checked={channelType === t.id}
                    onChange={() => setChannelType(t.id)}
                    className="accent-emerald-500"
                    disabled={busy}
                  />
                  <span className="text-fg">{t.label}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            className="rounded-xl border border-white/10 px-4 py-2 text-sm text-fg hover:text-white"
            onClick={onClose}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="button"
            className="rounded-xl bg-emerald-500 px-4 py-2 text-sm font-semibold text-emerald-950 disabled:opacity-60"
            onClick={() => onSubmit(name, channelType)}
            disabled={busy || !name.trim() || !channelType}
          >
            {busy ? "Creating..." : "Create"}
          </button>
        </div>
      </div>
    </div>
  );
}
